import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import { useDispositionQuery } from "../../../Services/ticket/CreateTicket";
import { USER_TEAM_ID } from "../../../constants";
import { toast } from "react-toastify";
import { useCreateYoutubeConfigurationMutation, useCreateYoutubeConfigurationVectorMutation, useCreateYoutubeConfigurationAiMutation } from "../../../Services/instagram/Instagram";

interface AddConfigurationModalProps {
  closeModal: () => void;
  refetchYoutubeData?: () => void;
  refetchVectorData?: () => void;
  refetchAiData?: () => void;
}

const AddConfigurationModal: React.FC<AddConfigurationModalProps> = ({
  closeModal,
  refetchYoutubeData,
  refetchVectorData,
  refetchAiData,
}) => {
  const steps = [
    'YouTube configuration',
    'Create configuration vector',
    'AI configuration',
  ];
  const teamId = Number(localStorage.getItem(USER_TEAM_ID)) || 0;
  const [triggerCreateYoutubeConfiguration] = useCreateYoutubeConfigurationMutation();
  const [triggerCreateYoutubeConfigurationVector] = useCreateYoutubeConfigurationVectorMutation();
  const [triggerCreateYoutubeConfigurationAi] = useCreateYoutubeConfigurationAiMutation();

  const initialValues = {
    // Step 1 fields
    channel_id: '',
    client_id: '',
    client_secret: '',

    token: '',
    refresh_token: '',
    token_expiry: '',
    reply_frequency: '',
    auto_ticket: false,
    auto_reply: false,
    ticket_msg: '',
    team: teamId, // Set team from localStorage
    disposition: '',
    disposition_ids: [], // Store the full path of selected disposition IDs

    // Step 2 fields
    vector_api_url: '',
    collection_name: '',
    token_data: '{}', // store as string, validate as JSON
    api_key: '',
  };

  const validationSchemas = [
    // Step 1: All fields required except ticket_msg, disposition
    Yup.object().shape({
      channel_id: Yup.string().required('Channel ID is required'),
      client_id: Yup.string().required('Client ID is required'),
      client_secret: Yup.string().required('Client Secret is required'),

      token: Yup.string().required('Token is required'),
      refresh_token: Yup.string().required('Refresh Token is required'),
      token_expiry: Yup.string().required('Token Expiry is required'),
      reply_frequency: Yup.number().typeError('Reply Frequency must be a number').required('Reply Frequency is required'),
      auto_ticket: Yup.boolean(),
      auto_reply: Yup.boolean(),
      ticket_msg: Yup.string()
        .nullable()
        .when('auto_ticket', {
          is: true,
          then: (schema) => schema.required('Ticket Message is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
      disposition: Yup.string()
        .nullable()
        .when('auto_ticket', {
          is: true,
          then: (schema) => schema.required('Disposition is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
      disposition_ids: Yup.array()
        .when('auto_ticket', {
          is: true,
          then: (schema) => schema.min(1, 'Please select a disposition'),
          otherwise: (schema) => schema.notRequired(),
        }),
    }),
    // Step 2: channel_id, token_data (JSON), api_key
    Yup.object().shape({
      channel_id: Yup.string().required('Channel ID is required'),
      token_data: Yup.string()
        .test('is-json', 'Token Data must be valid JSON', value => {
          if (!value) return false;
          try {
            JSON.parse(value);
            return true;
          } catch {
            return false;
          }
        })
        .required('Token Data is required'),
      vector_api_url: Yup.string().required('Vector API URL is required'),
      collection_name: Yup.string().required('Collection Name is required'),
    }),
    // Step 3: AI Configuration
    Yup.object().shape({
      api_key: Yup.string().required('API Key is required')
    }),
  ];

  const [step, setStep] = useState(0);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [options, setOptions] = useState<any[]>([]);
  const [dispositionOptions, setDispositionOptions] = useState<any[]>([]);


  // Fetch disposition data
  const { data: dispositionData, isLoading: isDispositionLoading } = useDispositionQuery(teamId);

  // Initialize options when disposition data is loaded
  useEffect(() => {
    if (dispositionData) {
      setOptions(dispositionData);
      setDispositionOptions(dispositionData);
    }
  }, [dispositionData]);

  // Handle disposition selection change
  const handleDispositionChange = (e: React.ChangeEvent<HTMLSelectElement>, level: number) => {
    const selectedId = parseInt(e.target.value);
    const newSelectedIds = [...selectedIds];
    newSelectedIds[level] = selectedId;
    newSelectedIds.length = level + 1; // Remove any deeper selections
    setSelectedIds(newSelectedIds);

    // Update form values
    const selectedDisposition = options.find(opt => opt.id === selectedId);
    if (selectedDisposition) {
      formik.setFieldValue('disposition', selectedDisposition.topic);
      formik.setFieldValue('disposition_ids', newSelectedIds);

      // Update options for next level dropdown
      if (selectedDisposition.sub_topics && selectedDisposition.sub_topics.length > 0) {
        setOptions(selectedDisposition.sub_topics);
      } else {
        setOptions([]);
      }
    }
  };

  // Render disposition dropdowns recursively
  const renderDispositionDropdowns = (data: any[], level: number = 0) => {
    if (!data || data.length === 0 || !formik.values.auto_ticket) return null;

    return (
      <div key={`disposition-level-${level}`} className="mb-3">
        <label className="form-label">
          {level === 0 ? 'Disposition' : 'Sub Disposition'}
          {level === 0 && <span className="text-danger">*</span>}
        </label>
        <select
          className={`form-select ${formik.touched.disposition && formik.errors.disposition ? 'is-invalid' : ''}`}
          value={selectedIds[level] || ''}
          onChange={(e) => handleDispositionChange(e, level)}
          onBlur={formik.handleBlur}
          name={`disposition-level-${level}`}
        >
          <option value="">Select {level === 0 ? 'Disposition' : 'Sub Disposition'}</option>
          {data.map((option) => (
            <option key={option.id} value={option.id}>
              {option.topic}
            </option>
          ))}
        </select>
        {level === 0 && formik.touched.disposition && formik.errors.disposition && (
          <div className="invalid-feedback">{formik.errors.disposition}</div>
        )}
        {selectedIds[level] && data.find(opt => opt.id === selectedIds[level])?.sub_topics && (
          <div className="mt-2">
            {renderDispositionDropdowns(
              data.find(opt => opt.id === selectedIds[level])?.sub_topics || [],
              level + 1
            )}
          </div>
        )}
      </div>
    );
  };

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchemas[step],
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      if (step < steps.length - 1) {
        setStep((s) => s + 1);
      } else {
        // Handle the final step submission (AI Configuration)
        try {
          const aiConfigData = {
            team: teamId,
            api_key: values.api_key
          };

          const result = await triggerCreateYoutubeConfigurationAi({ body: aiConfigData });
          if ('error' in result && result.error) {
            throw new Error('Failed to save AI configuration');
          }

          // Show success message for AI configuration
          toast.success('AI Configuration saved successfully!');
          refetchAiData?.();
          closeModal();
        } catch (error) {
          console.error('Error saving AI configuration:', error);
          toast.error('Failed to save AI configuration. Please try again.');
        }
      }
    },
    enableReinitialize: true,
  });

  // Reset disposition state when auto_ticket is toggled off
  useEffect(() => {
    if (!formik.values.auto_ticket) {
      setSelectedIds([]);
      formik.setFieldValue('disposition', '');
      formik.setFieldValue('disposition_ids', []);
    }
  }, [formik.values.auto_ticket]);

  const handleNext = async () => {
    const valid = await formik.validateForm();
    formik.setTouched({
      ...Object.keys(formik.values).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {}),
    });

    if (Object.keys(valid).length === 0) {
      try {
        if (step === 0) {
          // First step - save YouTube configuration
          const firstStepData = {
            channel_id: formik.values.channel_id,
            client_id: formik.values.client_id,
            client_secret: formik.values.client_secret,
            token: formik.values.token,
            refresh_token: formik.values.refresh_token,
            token_expiry: formik.values.token_expiry,
            reply_frequency: formik.values.reply_frequency,
            auto_ticket: formik.values.auto_ticket,
            auto_reply: formik.values.auto_reply,
            ticket_msg: formik.values.ticket_msg,
            team: teamId,
            // Get the last selected ID from disposition_ids array
            disposition: formik.values.disposition_ids?.length > 0
              ? formik.values.disposition_ids[formik.values.disposition_ids.length - 1]
              : '',
            api_key: formik.values.api_key
          };

          try {
            const result = await triggerCreateYoutubeConfiguration({ body: firstStepData });

            // If there's an error in the response
            if ('error' in result && result.error) {
              // Type guard to check if it's a FetchBaseQueryError with data
              if ('data' in result.error && result.error.data) {
                const errorData = result.error.data as { non_field_errors?: string[] };
                // Handle specific error case where configuration already exists
                if (errorData.non_field_errors?.some(err =>
                  err.includes('A Youtube Configuration already exists.')
                )) {
                  toast.error('A YouTube Configuration already exists for this team.');
                  return; // Prevent moving to next step
                }
              }
              throw new Error('Failed to save configuration');
            }

            // Show success message for YouTube configuration
            toast.success('YouTube Configuration saved successfully!');
            refetchYoutubeData?.();
          } catch (error) {
            console.error('Error in YouTube configuration:', error);
            throw error; // Re-throw to be caught by the outer try-catch
          }
        } else if (step === 1) {
          // Second step - save vector configuration
          const vectorConfigData = {
            vector_api_url: formik.values.vector_api_url,
            collection_name: formik.values.collection_name,
            team: teamId // Ensure team ID is a number
          };

          try {
            const result = await triggerCreateYoutubeConfigurationVector({ body: vectorConfigData });

            // If there's an error in the response
            if ('error' in result && result.error) {
              // Type guard to check if it's a FetchBaseQueryError with data
              if ('data' in result.error && result.error.data) {
                const errorData = result.error.data as { non_field_errors?: string[] };
                // Handle specific error case where vector configuration already exists
                if (errorData.non_field_errors?.some(err =>
                  err.includes('A Vector DB Configuration already exists')
                )) {
                  toast.error('A Vector DB Configuration already exists for this team.');
                  return; // Prevent moving to next step
                }
              }
              throw new Error('Failed to save vector configuration');
            }

            // Show success message for Vector configuration
            refetchVectorData?.();
            toast.success('Vector Configuration saved successfully!');
          } catch (error) {
            console.error('Error in Vector configuration:', error);
            if (!error.message?.includes('A Vector DB Configuration already exists')) {
              toast.error('Failed to save vector configuration. Please try again.');
            }
            throw error; // Re-throw to be caught by the outer try-catch
          }
        } else if (step === 2) {
          // This block is no longer needed as the submission is now handled in formik's onSubmit
          // Just move to the next step which will trigger the form submission
          return;
        }

        // Only move to next step if there were no errors
        setStep((s) => s + 1);
      } catch (error) {
        console.error('Error saving configuration:', error);
        // The specific error message is already shown in the error handling above
        if (!error.message?.includes('A Youtube Configuration already exists')) {
          toast.error('Failed to save configuration. Please try again.');
        }
      }
    }
  };

  const handleBack = () => {
    setStep((s) => s - 1);
  };

  const handleCancel = () => {
    closeModal();
  };

  return (
    <div>
      <h4 className="text-center">Create youTube configuration</h4>
      <div className="mb-3 text-center">
        <span>Step {step + 1} of {steps.length}: <b>{steps[step]}</b></span>
      </div>
      <form onSubmit={formik.handleSubmit}>
        {step === 0 && (
          <>
            <div className="form-group mb-3">
              <label className="required">Channel ID</label>
              <input type="text" className={clsx("form-control", { "is-invalid": formik.touched.channel_id && formik.errors.channel_id })}
                name="channel_id" value={formik.values.channel_id} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.channel_id && formik.errors.channel_id && <div className="invalid-feedback">{formik.errors.channel_id}</div>}
            </div>
            <div className="form-group mb-3">
              <label className="required">Client ID</label>
              <input type="text" className={clsx("form-control", { "is-invalid": formik.touched.client_id && formik.errors.client_id })}
                name="client_id" value={formik.values.client_id} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.client_id && formik.errors.client_id && <div className="invalid-feedback">{formik.errors.client_id}</div>}
            </div>
            <div className="form-group mb-3">
              <label className="required">Client secret</label>
              <input type="text" className={clsx("form-control", { "is-invalid": formik.touched.client_secret && formik.errors.client_secret })}
                name="client_secret" value={formik.values.client_secret} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.client_secret && formik.errors.client_secret && <div className="invalid-feedback">{formik.errors.client_secret}</div>}
            </div>

            <div className="form-group mb-3">
              <label className="required">Token</label>
              <input type="text" className={clsx("form-control", { "is-invalid": formik.touched.token && formik.errors.token })}
                name="token" value={formik.values.token} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.token && formik.errors.token && <div className="invalid-feedback">{formik.errors.token}</div>}
            </div>
            <div className="form-group mb-3">
              <label className="required">Refresh token</label>
              <input type="text" className={clsx("form-control", { "is-invalid": formik.touched.refresh_token && formik.errors.refresh_token })}
                name="refresh_token" value={formik.values.refresh_token} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.refresh_token && formik.errors.refresh_token && <div className="invalid-feedback">{formik.errors.refresh_token}</div>}
            </div>
            <div className="form-group mb-3">
              <label className="required">Token expiry</label>
              <input type="datetime-local" className={clsx("form-control", { "is-invalid": formik.touched.token_expiry && formik.errors.token_expiry })}
                name="token_expiry" value={formik.values.token_expiry} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.token_expiry && formik.errors.token_expiry && <div className="invalid-feedback">{formik.errors.token_expiry}</div>}
            </div>
            <div className="form-group mb-3">
              <label className="required">Reply frequency</label>
              <input type="number" className={clsx("form-control", { "is-invalid": formik.touched.reply_frequency && formik.errors.reply_frequency })}
                name="reply_frequency" value={formik.values.reply_frequency} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.reply_frequency && formik.errors.reply_frequency && <div className="invalid-feedback">{formik.errors.reply_frequency}</div>}
            </div>
            <div className="form-group mb-3 d-flex align-items-center">
              <label className="mb-0">Auto ticket</label>
              <input type="checkbox" className="form-check-input ms-2" name="auto_ticket" checked={formik.values.auto_ticket} onChange={formik.handleChange} />
              <label className="mb-0 ms-4">Auto reply</label>
              <input type="checkbox" className="form-check-input ms-2" name="auto_reply" checked={formik.values.auto_reply} onChange={formik.handleChange} />
            </div>
            {formik.values.auto_ticket && (
              <>
                <div className="form-group mb-3">
                  <label className="required">Ticket message</label>
                  <input type="text" className={clsx("form-control", { "is-invalid": formik.touched.ticket_msg && formik.errors.ticket_msg })}
                    name="ticket_msg" value={formik.values.ticket_msg} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                  {formik.touched.ticket_msg && formik.errors.ticket_msg && <div className="invalid-feedback">{formik.errors.ticket_msg}</div>}
                </div>
                <div className="form-group mb-3">
                  {isDispositionLoading ? (
                    <div className="d-flex align-items-center">
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span>Loading dispositions...</span>
                    </div>
                  ) : (
                    renderDispositionDropdowns(dispositionOptions)
                  )}
                </div>
              </>
            )}

          </>
        )}
        {step === 1 && (
          <>
            <div className="form-group mb-3">
              <label className="required">Vector API URL</label>
              <input
                type="text"
                className={clsx("form-control", { "is-invalid": formik.touched.vector_api_url && formik.errors.vector_api_url })}
                name="vector_api_url"
                value={formik.values.vector_api_url}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.vector_api_url && formik.errors.vector_api_url && (
                <div className="invalid-feedback">{formik.errors.vector_api_url}</div>
              )}
            </div>
            <div className="form-group mb-3">
              <label className="required">Collection name</label>
              <input
                type="text"
                className={clsx("form-control", { "is-invalid": formik.touched.collection_name && formik.errors.collection_name })}
                name="collection_name"
                value={formik.values.collection_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.collection_name && formik.errors.collection_name && (
                <div className="invalid-feedback">{formik.errors.collection_name}</div>
              )}
            </div>
            <input type="hidden" name="team" value={formik.values.team} />
          </>
        )}
        {step === 2 && (
          <>
            <div className="form-group mb-3">
              <label className="required">API key</label>
              <input
                type="password"
                className={clsx("form-control", { "is-invalid": formik.touched.api_key && formik.errors.api_key })}
                name="api_key"
                value={formik.values.api_key}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter your AI API key"
              />
              {formik.touched.api_key && formik.errors.api_key && (
                <div className="invalid-feedback">{formik.errors.api_key}</div>
              )}
            </div>
          </>
        )}
        <div className="d-flex justify-content-end mt-6">
          <button type="button" className="btn btn-secondary me-2" onClick={handleCancel}>Cancel</button>
          {step > 0 && (<button type="button" className="btn btn-outline-primary me-2" onClick={handleBack}>Back</button>)}
          {step < steps.length - 1 && (<button type="button" className="btn btn-primary" onClick={handleNext}>Next</button>)}
          {step === steps.length - 1 && (<button type="submit" className="btn btn-primary">Save</button>)}
        </div>
      </form>
    </div>
  );
};

export default AddConfigurationModal;
